import React from "react";
import { Sun, Moon, GitCompareArrows, Boxes, FileText, ScanLine, ClipboardList, Layers, LogOut } from "lucide-react";
import { siteLogout } from "@/components/SiteGate";
import { useApp } from "@/context/AppContext";
import { LANGS } from "@/i18n/translations";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";

export default function Header() {
  const { t, theme, toggleTheme, lang, setLang, view, setView } = useApp();

  const navBtn = (id, label, Icon, suffix = "") => (
    <button
      data-testid={`nav-${id}${suffix}`}
      onClick={() => setView(id)}
      className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-xs font-semibold transition-colors ${
        view === id ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-40 border-b border-border bg-background"
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
        <div className="flex h-16 items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Logo tagline={t("tagline")} />
            <nav className="ml-4 hidden items-center border border-border md:flex">
              {navBtn("compare", t("navCompare"), GitCompareArrows)}
              {navBtn("quote", t("navQuote"), FileText)}
              {navBtn("specs", t("navSpecs"), ClipboardList)}
              {navBtn("systems", t("navSystems"), Layers)}
              {navBtn("measure", t("navMeasure"), ScanLine)}
              {navBtn("manage", t("navCatalog"), Boxes)}
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <div
              data-testid="language-switcher"
              className="flex items-center border border-border"
            >
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`px-2.5 py-1.5 text-xs font-semibold font-mono transition-colors ${
                    lang === l.code
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <Button
              data-testid="theme-toggle"
              variant="outline"
              size="icon"
              onClick={toggleTheme}
              className="rounded-none border-border"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button
              data-testid="site-logout-btn"
              variant="outline"
              onClick={siteLogout}
              title={t("logout")}
              className="rounded-none border-border px-2.5 text-xs font-semibold"
            >
              <LogOut className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">{t("logout")}</span>
            </Button>
          </div>
        </div>
        <nav className="flex items-center gap-2 overflow-x-auto border-t border-border py-2 md:hidden">
          {navBtn("compare", t("navCompare"), GitCompareArrows, "-mobile")}
          {navBtn("quote", t("navQuote"), FileText, "-mobile")}
          {navBtn("specs", t("navSpecs"), ClipboardList, "-mobile")}
          {navBtn("systems", t("navSystems"), Layers, "-mobile")}
          {navBtn("measure", t("navMeasure"), ScanLine, "-mobile")}
          {navBtn("manage", t("navCatalog"), Boxes, "-mobile")}
        </nav>
      </div>
    </header>
  );
}
